import { AgingReport, AgingReportMessage } from "common";
import moment from "moment";

export type AlertKind = "overdue" | "message";

export interface ClaimAlert {
  kind: AlertKind;
  /** Stable across refreshes, so React keys stay put */
  id: string;
  facility: string;
  payer: string;
  sk: string;
  residentName: string;
  billType: string;
  /** Overdue: the follow-up date. New message: who wrote it */
  detail: string;
  /** ISO time the message or follow-up was written, used for ordering */
  at: string;
  text?: string;
  /** Overdue: who wrote the latest message of the thread (for messages this is `detail`) */
  author?: string;
}

/** Alerts matching every word of `query` in the resident, bill type, facility, payer, author or message text */
export const filterAlerts = (alerts: ClaimAlert[], query: string) => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return alerts;
  return alerts.filter((alert) => {
    const text = [alert.residentName, alert.billType, alert.facility, alert.payer, alert.detail, alert.author, alert.text].join(" ").toLowerCase();
    return words.every((word) => text.includes(word));
  });
};

/** Unreadable or missing times sort last */
const timeOf = (value: string) => {
  const time = Date.parse(value);
  return Number.isNaN(time) ? -Infinity : time;
};

/** Overdue follow-ups before messages, and within each the most recent first */
export const compareNewestFirst = (a: ClaimAlert, b: ClaimAlert) => {
  if (a.kind !== b.kind) return a.kind === "overdue" ? -1 : 1;
  const difference = timeOf(b.at) - timeOf(a.at);
  return Number.isNaN(difference) ? 0 : difference;
};

const activeResponses = (message: AgingReportMessage) => (message.responses ?? []).filter((response) => !response.archivedAt);

// A paid or zero-balance claim has nothing left to follow up on. Months are optional on a message; without any it covers the whole claim.
const hasOpenClaim = (report: AgingReport, message: AgingReportMessage) =>
  (message.months?.length ? message.months : Object.keys(report.balances ?? {})).some((month) => {
    const balance = report.balances?.[month];
    return Boolean(balance && balance.history?.[0] > 0 && !balance.paid);
  });

const isInvolved = (username: string, message: AgingReportMessage) =>
  [message.author, ...(message.recipients ?? []), ...activeResponses(message).flatMap((r) => [r.author, ...(r.recipients ?? [])])].includes(username);

export interface ReportAlertContext {
  username: string;
  isAdmin: boolean;
  facility: string;
  payer: string;
}

/** A message or reply from someone else this recent counts as new */
export const NEW_WINDOW_HOURS = 48;

export interface ThreadStatus {
  /** The latest message or reply has a follow-up date that has passed, on a claim that still has a balance */
  overdue: boolean;
  /** The date the thread is waiting on (the latest reply's, else the message's) */
  deadline?: string;
  /** The latest message or reply, which is what the date and the author above belong to */
  latest: AgingReportMessage | AgingReportMessage["responses"][number];
  /** Someone other than `username` wrote the latest message or reply within the last NEW_WINDOW_HOURS */
  isNew: boolean;
}

export const getThreadStatus = (report: AgingReport, message: AgingReportMessage, username?: string, now = Date.now()): ThreadStatus => {
  const responses = activeResponses(message);
  const latest = responses.length ? responses[responses.length - 1] : message;
  const deadline = latest.deadline;
  const overdue = Boolean(deadline && hasOpenClaim(report, message) && moment(now).startOf("day").isSameOrAfter(moment(deadline)));
  const writtenAt = Date.parse(latest.createdAt ?? "");
  const isNew = !Number.isNaN(writtenAt) && latest.author !== username && now - writtenAt < NEW_WINDOW_HOURS * 3600 * 1000;
  return { overdue, deadline, latest, isNew };
};

/** Alerts for one report: follow-ups that have turned orange, and every message */
export const getReportAlerts = (
  report: AgingReport,
  { username, isAdmin, facility, payer }: ReportAlertContext
): ClaimAlert[] => {
  if (report.archived) return [];
  const base = { facility, payer, sk: report.sk, residentName: report.residentName, billType: report.billType };

  return (report.messages ?? []).flatMap((message) => {
    if (message.archivedAt || (!isAdmin && !isInvolved(username, message))) return [];
    const alerts: ClaimAlert[] = [];
    const { overdue, deadline, latest } = getThreadStatus(report, message);

    if (overdue && deadline) {
      // Ordered by when the follow-up was written, so one added just now is on top even if others share its date
      alerts.push({ ...base, kind: "overdue", id: `overdue:${report.sk}:${message.id}:${deadline}`, detail: deadline, at: latest.createdAt ?? deadline, text: latest.message, author: latest.author });
    }

    [message, ...activeResponses(message)].forEach((item) => {
      alerts.push({ ...base, kind: "message", id: `message:${item.id}`, detail: item.author, at: item.createdAt ?? "", text: item.message });
    });
    return alerts;
  });
};
